<template>
  <div>
    <div class="card card-form sign-upIn">
      <div class="card-header">
        <div class="card-title">Change Password</div>
      </div>
      <div class="card-body">
        <form @submit.prevent="handleSubmit" class="base-form">
          <h5 class="form-title mb-3">Update your password</h5>

          <!-- Current Password (if logged in) -->
          <div v-if="isLoggedIn" class="input-group _ig-transparent mb-3">
            <span class="input-group-text"><i class="fa fa-lock"></i></span>
            <input :type="showCurrentPassword ? 'text' : 'password'" v-model="form.current_password"
              name="current_password" id="current_password" class="form-control" placeholder="Current password"
              maxlength="32" required />
            <span @click="showCurrentPassword = !showCurrentPassword" class="input-group-text cursor-pointer"
              style="cursor: pointer;">
              <i :class="showCurrentPassword ? 'fa fa-eye' : 'fa fa-eye-slash'"></i>
            </span>
          </div>

          <!-- New Password -->
          <div class="input-group _ig-transparent mb-3">
            <span class="input-group-text"><i class="fa fa-key"></i></span>
            <input :type="showNewPassword ? 'text' : 'password'" v-model="form.new_password" name="new_password"
              id="new_password" class="form-control" autocomplete="new-password"
              placeholder="New password (8-32 chars, Upper, Lower, Number, Symbol)" maxlength="32" required />
            <span @click="showNewPassword = !showNewPassword" class="input-group-text cursor-pointer"
              style="cursor: pointer;">
              <i :class="showNewPassword ? 'fa fa-eye' : 'fa fa-eye-slash'"></i>
            </span>
          </div>

          <!-- Password Requirements Checklist -->
          <div v-if="form.new_password" class="password-requirements mb-3 p-2 border rounded bg-light"
            style="font-size: 12px;">
            <div class="fw-bold mb-1 text-secondary">Password requirements:</div>
            <div class="row row-cols-2 g-1">
              <div
                :class="passwordValidation.checks.minLength && passwordValidation.checks.maxLength ? 'text-success' : 'text-danger'">
                <i
                  :class="passwordValidation.checks.minLength && passwordValidation.checks.maxLength ? 'fas fa-check-circle me-1' : 'fas fa-times-circle me-1'"></i>
                8 - 32 characters
              </div>
              <div :class="passwordValidation.checks.hasUpper ? 'text-success' : 'text-danger'">
                <i
                  :class="passwordValidation.checks.hasUpper ? 'fas fa-check-circle me-1' : 'fas fa-times-circle me-1'"></i>
                1 Uppercase (A-Z)
              </div>
              <div :class="passwordValidation.checks.hasLower ? 'text-success' : 'text-danger'">
                <i
                  :class="passwordValidation.checks.hasLower ? 'fas fa-check-circle me-1' : 'fas fa-times-circle me-1'"></i>
                1 Lowercase (a-z)
              </div>
              <div :class="passwordValidation.checks.hasNumber ? 'text-success' : 'text-danger'">
                <i
                  :class="passwordValidation.checks.hasNumber ? 'fas fa-check-circle me-1' : 'fas fa-times-circle me-1'"></i>
                1 Number (0-9)
              </div>
              <div :class="passwordValidation.checks.hasSymbol ? 'text-success' : 'text-danger'">
                <i
                  :class="passwordValidation.checks.hasSymbol ? 'fas fa-check-circle me-1' : 'fas fa-times-circle me-1'"></i>
                1 Symbol (!@#$...)
              </div>
            </div>
          </div>

          <!-- Confirm New Password -->
          <div class="input-group _ig-transparent mb-1">
            <span class="input-group-text"><i class="fa fa-lock"></i></span>
            <input :type="showConfirmPassword ? 'text' : 'password'" v-model="confirm_password" id="confirm_password"
              class="form-control" placeholder="Confirm new password" maxlength="32" required />
            <span @click="showConfirmPassword = !showConfirmPassword" class="input-group-text cursor-pointer"
              style="cursor: pointer;">
              <i :class="showConfirmPassword ? 'fa fa-eye' : 'fa fa-eye-slash'"></i>
            </span>
          </div>
          <div v-show="confirmMismatch" class="mb-2" style="margin-bottom: 5px; font-size: 13px; color: red">
            Confirm password mismatched.
          </div>

          <div class="form-footer mt-4 d-flex justify-content-end">
            <button type="submit" class="btn btn-success"
              :disabled="loading || !passwordValidation.isValid || confirmMismatch">
              Update Password
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { validatePassword } from "@/utils/passwordValidator";

export default {
  name: "ChangePassword",
  data() {
    return {
      loadingInstance: null,
      loading: false,
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      confirm_password: "",
      confirmMismatch: false,
      form: {
        current_password: "",
        new_password: "",
      },
    };
  },
  watch: {
    confirm_password(val) {
      if (val === this.form.new_password && val !== "") {
        this.confirmMismatch = false;
      } else {
        this.confirmMismatch = true;
      }
    },
    "form.new_password"(val) {
      if (this.confirm_password !== "") {
        this.confirmMismatch = this.confirm_password !== val;
      }
    },
  },
  computed: {
    isLoggedIn() {
      return this.$store.getters["auth/getLoggedIn"];
    },
    passwordValidation() {
      return validatePassword(this.form.new_password);
    },
  },
  methods: {
    showLoading() {
      this.loadingInstance = this.$loading({
        lock: true,
        text: "Updating...",
        background: "rgba(0, 0, 0, 0.7)",
        fullscreen: true,
      });
    },
    hideLoading() {
      if (this.loadingInstance) {
        this.loadingInstance.close();
      }
    },
    handleSubmit() {
      if (!this.passwordValidation.isValid) {
        this.$toast.error(
          "Password must be 8-32 characters long and include at least one uppercase letter, one lowercase letter, one number, and one symbol."
        );
        return;
      }

      if (this.confirmMismatch || this.confirm_password !== this.form.new_password) {
        this.confirmMismatch = true;
        this.$toast.error("Confirm password mismatched.");
        return;
      }

      this.showLoading();
      this.loading = true;

      // Dispatch to Vuex store or API call
      const payload = {
        current_password: this.form.current_password,
        new_password: this.form.new_password,
        password: this.form.new_password,
      };

      this.$store
        .dispatch("auth/changePassword", payload)
        .then((res) => {
          if (res && (res.status == 1 || res.data?.status == 1)) {
            this.$toast.success(res.message || res.data?.message || "Password updated successfully.");
            this.form.current_password = "";
            this.form.new_password = "";
            this.confirm_password = "";
          } else {
            const errorMsg = res?.message || res?.data?.message || "Failed to update password.";
            this.$toast.error(errorMsg);
          }
        })
        .catch((err) => {
          console.error(err);
          const errorMsg =
            err.response?.data?.message || err.message || "Something went wrong. Please try again.";
          this.$toast.error(errorMsg);
        })
        .finally(() => {
          this.hideLoading();
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
